import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import PessoaAutocomplete from '../../components/PessoaAutocomplete';
import ComboBoxBusca from '../../components/ComboBoxBusca';
import protocoloService from '../../services/protocoloService';
import { extensaoService } from '../../services/extensaoService';
import cursoService from '../../services/cursoService';

/**
 * Formulário do protocolo de requerimento, compartilhado por criar e editar.
 *
 * Há requerentes que não existem no cadastro — é o motivo de o registro
 * manual existir —, então o nome livre é um caminho de primeira classe, e
 * não uma exceção escondida.
 */

const rotuloExtensao = (ext) => {
  // Há extensões homônimas em estados diferentes (Campina Grande fica em PA
  // e em PB). Só o nome não permite escolher a certa.
  const local = [ext.cidade, ext.uf].filter(Boolean).join('/');
  return local ? `${ext.nome} — ${local}` : ext.nome;
};

const hoje = () => new Date().toISOString().slice(0, 10);

const VALORES_INICIAIS = {
  requerente_pessoa: null,
  requerente_nome: '',
  tipo_requerimento: '',
  curso: '',
  extensao: '',
  data_entrada: hoje(),
  observacoes: ''
};

const ProtocoloForm = ({ titulo, protocoloId = null, valoresIniciais = null, onSalvar }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState(valoresIniciais || VALORES_INICIAIS);
  // Num protocolo novo o caminho comum é o aluno já cadastrado. Abrir no nome
  // livre faz o caminho de menor esforço ser digitar à mão o nome de quem já
  // existe — e aí o protocolo deixa de apontar para a pessoa certa.
  // Na edição, vale o que o registro tem.
  const [requerenteCadastrado, setRequerenteCadastrado] = useState(
    valoresIniciais ? Boolean(valoresIniciais.requerente_pessoa) : true
  );
  const [tipos, setTipos] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [erros, setErros] = useState({});
  const [erroGeral, setErroGeral] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (valoresIniciais) {
      setForm(valoresIniciais);
      setRequerenteCadastrado(Boolean(valoresIniciais.requerente_pessoa));
    }
  }, [valoresIniciais]);

  useEffect(() => {
    const carregarApoio = async () => {
      try {
        const [respTipos, respExtensoes, respCursos] = await Promise.all([
          protocoloService.listarTipos(),
          extensaoService.list({ page: 1, limit: 1000 }),
          cursoService.list({ page: 1, limit: 1000 })
        ]);

        setTipos(respTipos.data || []);
        setExtensoes(respExtensoes.data || []);
        setCursos(respCursos.data || []);
      } catch (err) {
        setErroGeral(err.message || 'Não foi possível carregar os dados do formulário.');
      } finally {
        setCarregando(false);
      }
    };

    carregarApoio();
  }, []);

  const tipoSelecionado = tipos.find(t => t.valor === form.tipo_requerimento);
  const exigeCurso = Boolean(tipoSelecionado?.exigeCurso);

  const alterar = useCallback((campo, valor) => {
    setForm(anterior => ({ ...anterior, [campo]: valor }));
    setErros(anterior => {
      if (!anterior[campo]) return anterior;
      const { [campo]: _removido, ...resto } = anterior;
      return resto;
    });
  }, []);

  const handleChange = (e) => alterar(e.target.name, e.target.value);

  const trocarOrigemRequerente = (cadastrado) => {
    setRequerenteCadastrado(cadastrado);
    // Trocar de modo limpa o outro lado: deixar os dois preenchidos faz o
    // usuário achar que mandou um e mandar outro.
    alterar('requerente_pessoa', null);
    alterar('requerente_nome', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (salvando) return;

    setErros({});
    setErroGeral('');
    setSalvando(true);

    try {
      const payload = {
        ...form,
        curso: exigeCurso ? form.curso || null : null,
        requerente_pessoa: requerenteCadastrado ? form.requerente_pessoa : null,
        requerente_nome: requerenteCadastrado ? '' : (form.requerente_nome || '').trim()
      };

      await onSalvar(payload);
    } catch (err) {
      if (err.fieldErrors) {
        setErros(err.fieldErrors);
        setErroGeral('Confira os campos destacados.');
      } else {
        setErroGeral(err.message || 'Não foi possível salvar o protocolo.');
      }
      setSalvando(false);
    }
  };

  const erroDe = (campo) => (Array.isArray(erros[campo]) ? erros[campo][0] : erros[campo]);

  const classeCampo = (campo) =>
    `w-full min-h-11 px-3 py-2 rounded border focus:outline-none focus:ring-2 ${
      erroDe(campo)
        ? 'border-red-500 focus:ring-red-400'
        : 'border-gray-300 focus:ring-green-400'
    }`;

  const MensagemErro = ({ campo }) =>
    erroDe(campo) ? <p className="mt-1 text-sm text-red-600">{erroDe(campo)}</p> : null;

  if (carregando) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64 text-gray-600">
            Carregando formulário...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{titulo}</h2>
          {/* Volta para a listagem de protocolos, não para o dashboard. */}
          <button
            type="button"
            onClick={() => navigate('/protocolos')}
            className="self-start sm:self-auto min-h-11 px-4 py-2 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Voltar para a listagem</span>
          </button>
        </div>

        {erroGeral && (
          <div className="mb-4 px-4 py-3 rounded bg-red-50 border border-red-200 text-red-700">
            {erroGeral}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded shadow p-4 sm:p-6 max-w-3xl">
          <fieldset className="mb-6">
            <legend className="text-sm font-semibold text-gray-700 mb-2">Requerente</legend>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 mb-3">
              {/* O alvo de toque é o rótulo inteiro (min-h-11 = 44px), não só
                  o círculo do radio: no celular o dedo erra um alvo de 16px. */}
              <label className="flex items-center gap-2 cursor-pointer min-h-11 py-2 pr-3">
                <input
                  type="radio"
                  name="origemRequerente"
                  checked={requerenteCadastrado}
                  onChange={() => trocarOrigemRequerente(true)}
                  className="w-5 h-5"
                />
                <span className="text-sm text-gray-700">É aluno cadastrado</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer min-h-11 py-2 pr-3">
                <input
                  type="radio"
                  name="origemRequerente"
                  checked={!requerenteCadastrado}
                  onChange={() => trocarOrigemRequerente(false)}
                  className="w-5 h-5"
                />
                <span className="text-sm text-gray-700">Não é aluno cadastrado</span>
              </label>
            </div>

            {requerenteCadastrado ? (
              <div>
                {/* O requerimento é do aluno, então a busca é restrita ao
                    cadastro de alunos. Requerente que ainda não é aluno
                    cadastrado entra pelo nome livre, ao lado. */}
                <PessoaAutocomplete
                  value={form.requerente_pessoa}
                  onChange={(id) => alterar('requerente_pessoa', id)}
                  dtype="Aluno"
                  placeholder="Digite o nome, matrícula ou CPF do aluno..."
                  textoVazio="Nenhum aluno encontrado"
                />
                <MensagemErro campo="requerente_nome" />
              </div>
            ) : (
              <div>
                <label htmlFor="requerente_nome" className="block text-sm text-gray-700 mb-1">
                  Nome do requerente <span className="text-red-600">*</span>
                </label>
                <input
                  id="requerente_nome"
                  name="requerente_nome"
                  type="text"
                  maxLength={255}
                  value={form.requerente_nome}
                  onChange={handleChange}
                  className={classeCampo('requerente_nome')}
                  placeholder="Nome completo do requerente"
                />
                <MensagemErro campo="requerente_nome" />
              </div>
            )}
          </fieldset>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="tipo_requerimento" className="block text-sm text-gray-700 mb-1">
                Tipo de requerimento <span className="text-red-600">*</span>
              </label>
              <select
                id="tipo_requerimento"
                name="tipo_requerimento"
                value={form.tipo_requerimento}
                onChange={handleChange}
                className={classeCampo('tipo_requerimento')}
              >
                <option value="">Selecione...</option>
                {tipos.map(tipo => (
                  <option key={tipo.valor} value={tipo.valor}>{tipo.rotulo}</option>
                ))}
              </select>
              <MensagemErro campo="tipo_requerimento" />
            </div>

            {exigeCurso && (
              <div>
                <label htmlFor="curso" className="block text-sm text-gray-700 mb-1">
                  Curso <span className="text-red-600">*</span>
                </label>
                <select
                  id="curso"
                  name="curso"
                  value={form.curso || ''}
                  onChange={handleChange}
                  className={classeCampo('curso')}
                >
                  <option value="">Selecione...</option>
                  {cursos.map(curso => (
                    <option key={curso.idCurso} value={curso.idCurso}>{curso.nome}</option>
                  ))}
                </select>
                <MensagemErro campo="curso" />
              </div>
            )}

            <div>
              <label htmlFor="extensao" className="block text-sm text-gray-700 mb-1">
                Extensão <span className="text-red-600">*</span>
              </label>
              {/* São mais de 300 extensões: rolar a lista procurando o nome
                  era o que a secretaria pedia para melhorar. */}
              <ComboBoxBusca
                id="extensao"
                name="extensao"
                value={form.extensao || ''}
                onChange={(valor) => alterar('extensao', valor)}
                opcoes={extensoes.map(ext => ({ valor: ext.idExtensao, rotulo: rotuloExtensao(ext) }))}
                placeholderBusca="Digite o nome da extensão..."
                textoVazio="Nenhuma extensão encontrada"
                erro={Boolean(erroDe('extensao'))}
              />
              <MensagemErro campo="extensao" />
            </div>

            <div>
              <label htmlFor="data_entrada" className="block text-sm text-gray-700 mb-1">
                Data de entrada <span className="text-red-600">*</span>
              </label>
              <input
                id="data_entrada"
                name="data_entrada"
                type="date"
                value={form.data_entrada || ''}
                onChange={handleChange}
                className={classeCampo('data_entrada')}
              />
              <MensagemErro campo="data_entrada" />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="observacoes" className="block text-sm text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              id="observacoes"
              name="observacoes"
              rows={3}
              maxLength={2000}
              value={form.observacoes || ''}
              onChange={handleChange}
              className={classeCampo('observacoes')}
            />
            <MensagemErro campo="observacoes" />
          </div>

          {!protocoloId && (
            <p className="mt-4 text-sm text-gray-600">
              O número do protocolo é gerado ao salvar e continua a numeração do livro.
            </p>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={salvando}
              className="min-h-11 px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={salvando ? faSpinner : faSave} spin={salvando} />
              <span>{salvando ? 'Salvando...' : 'Salvar'}</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/protocolos')}
              className="min-h-11 px-4 py-2 rounded border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProtocoloForm;
